import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPinIcon, 
  ChatBubbleLeftIcon, 
  UserGroupIcon, 
  UserPlusIcon, 
  UserMinusIcon,
  CheckBadgeIcon,
  SparklesIcon,
  StarIcon,
  PhotoIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { User } from '../../types';
import Avatar from '../common/Avatar';

import { resolveImageUrl, getOptimizedImageUrl } from '../../utils/image';
import { getUserRoleLabel } from '../../utils/format';
import { useImpressionTracker } from '../../hooks/useImpressionTracker';

interface UserCardProps {
  user: User;
  currentUser?: User | null;
  onFollowToggle: (user: User) => void;
  onMessage: (user: User) => void;
  onCollaborate: (user: User) => void;
  onQuickView?: (user: User) => void;
  isFollowing?: boolean;
}

const UserCard: React.FC<UserCardProps> = ({ 
  user, 
  currentUser, 
  onFollowToggle, 
  onMessage, 
  onCollaborate,
  onQuickView,
  isFollowing 
}) => {
  const elementRef = useImpressionTracker(user.id);
  
  const getCollaborationBadge = () => {
    const count = user.collaborations_count || 0;
    
    if (count === 0) {
      return {
        text: 'New Talent',
        className: 'bg-white text-gray-900 border-gray-200',
        icon: SparklesIcon
      };
    } else if (count < 5) {
      return {
        text: `${count} Collabs`,
        className: 'bg-white text-gray-900 border-gray-200',
        icon: UserGroupIcon
      };
    } else {
      return {
        text: `${count}+ Collabs`,
        className: 'bg-black text-white border-black',
        icon: StarIcon
      };
    }
  };

  const badge = getCollaborationBadge();
  const BadgeIcon = badge.icon;

  // Get recent images from user's portfolio and collaborations
  // This assumes the backend will provide these in the user object
  const recentImages = user.recent_images_signed || [];
  const displayImages = recentImages
    .filter(img => img && img.trim() !== '')
    .map(img => resolveImageUrl(img))
    .slice(0, 4);

  return (
    <motion.div
      ref={elementRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-300 flex flex-col h-full"
    >
      {/* Profile Header */}
      <div className="p-5 pb-3">
        <div className="flex items-start justify-between mb-3">
          {/* Avatar & Info */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <Avatar
                src={user.profile_pic_signed || resolveImageUrl(user.profile_pic)}
                alt={`${user.first_name} ${user.last_name}`}
                size="lg"
                className="border-2 border-white dark:border-gray-700 shadow-sm"
                isDeleted={!!user.deleted_at || !!user.deletion_scheduled_at}
              />
              {user.is_online && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" title="Online" />
              )}
              {user.subscription_tier === 'premium' && (
                <div className="absolute -bottom-1 -right-1 bg-black text-white p-0.5 rounded-full shadow-sm border-2 border-white dark:border-gray-800" title="Verified">
                  <CheckBadgeIcon className="w-3 h-3" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <Link to={`/profile/${user.id}`} className="block group/link">
                <h3 className="text-base font-bold text-gray-900 dark:text-white transition-all decoration-2 decoration-black dark:decoration-white underline-offset-2">
                  {user.first_name} {user.last_name}
                </h3>
              </Link>
              
              <div className="flex items-center justify-between gap-2 mt-1 w-full">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-0.5 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-md shrink-0">
                  {getUserRoleLabel(user)}
                </span>
                {(user.rating ?? 0) > 0 && (user.review_count ?? 0) > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-700 ml-auto shrink-0 bg-gray-50/50 dark:bg-gray-800 px-2 py-0.5 rounded-md border border-gray-100/50 dark:border-gray-700/50">
                    <StarIconSolid className="w-3.5 h-3.5 text-black dark:text-white" />
                    <span className="font-medium text-gray-900 dark:text-gray-200">{user.rating!.toFixed(1)}</span>
                    <span className="text-gray-400">({user.review_count})</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFollowToggle(user);
              }}
              className={`p-1.5 rounded-lg transition-all duration-200 border ${
                isFollowing
                  ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white hover:bg-gray-800 dark:hover:bg-gray-200'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-black dark:hover:border-white'
              }`}
              title={isFollowing ? 'Unfollow' : 'Follow'}
            >
              {isFollowing ? (
                <UserMinusIcon className="w-4 h-4" />
              ) : (
                <UserPlusIcon className="w-4 h-4" />
              )}
            </button>
            {/* {(!currentUser || currentUser.id !== user.id) && user.is_collaborator && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMessage(user);
                }}
                className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all duration-200"
                title="Message"
              >
                <ChatBubbleLeftIcon className="w-4 h-4" />
              </button>
            )} */}
            {(!currentUser || currentUser.id !== user.id) && user.user_type !== 'hirer' && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCollaborate(user);
                }}
                className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all duration-200"
                title="Collaborate"
              >
                <UserGroupIcon className="w-4 h-4" />
              </button>
            )}
            {onQuickView && user.user_type === 'model' && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onQuickView(user);
                }}
                className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all duration-200"
                title="Quick View Specs"
              >
                <EyeIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Bio */}
        {user.bio && (
          <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {user.bio}
          </p>
        )}

        {/* Skills/Tags */}
        <div className="flex flex-wrap gap-1.5">
          {/* Collab Badge */}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${badge.className}`}>
            <BadgeIcon className="w-3 h-3 mr-1" />
            {badge.text}
          </span>
          
          {user.skills?.slice(0, 2).map((skill, i) => (
            <span 
              key={i}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-gray-600"
            >
              {typeof skill === 'string' ? skill : skill.name}
            </span>
          ))}
          {(user.skills?.length || 0) > 2 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-600">
              +{(user.skills?.length || 0) - 2}
            </span>
          )}
        </div>
      </div>

      {/* Recent Work Grid - Adaptive Layout */}
      <Link to={`/profile/${user.id}`} className="block px-5 pb-4">
        {displayImages.length === 0 ? (
          // No images - show message
          <div className="rounded-xl bg-gray-50 border-2 border-dashed border-gray-200 p-8 text-center">
            <PhotoIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 font-medium">No works uploaded yet</p>
          </div>
        ) : displayImages.length === 1 ? (
          // 1 image - full width
          <div className="rounded-xl overflow-hidden">
            <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 overflow-hidden group/img">
              <img
                src={displayImages[0]}
                alt="Work 1"
                className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-110"
                loading="lazy"
              />
            </div>
          </div>
        ) : displayImages.length === 2 ? (
          // 2 images - side by side
          <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
            {displayImages.map((image: string, index: number) => (
              <div 
                key={index}
                className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 overflow-hidden group/img"
              >
                <img
                  src={image}
                  alt={`Work ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-110"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        ) : displayImages.length === 3 ? (
          // 3 images - 1 large + 2 small
          <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
            <div className="row-span-2 aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden group/img">
              <img
                src={displayImages[0]}
                alt="Work 1"
                className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-110"
                loading="lazy"
              />
            </div>
            {displayImages.slice(1).map((image: string, index: number) => (
              <div 
                key={index + 1}
                className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden group/img"
              >
                <img
                  src={image}
                  alt={`Work ${index + 2}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-110"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        ) : (
          // 4 images - 2x2 grid
          <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
            {displayImages.map((image: string, index: number) => (
              <div 
                key={index}
                className="aspect-square bg-gray-100 overflow-hidden group/img"
              >
                <img
                  src={image}
                  alt={`Work ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-110"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}
      </Link>

      {/* Footer Info */}
      <div className="mt-auto px-5 pb-4 pt-2 border-t border-gray-50 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1 truncate max-w-[60%]">
          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{user.location || 'Remote'}</span>
        </div>
        {user.hourly_rate && user.hourly_rate > 0 && (
          <div className="font-medium text-gray-900 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
            ₹{user.hourly_rate}/hr
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default UserCard;
